import { Error } from '@mui/icons-material';
import { AxiosError } from 'axios';
import { useMemo } from 'react';
import type { ErrorBoundaryContextType } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { type ErrorResponse, isRouteErrorResponse, useNavigate, useRouteError } from 'react-router';

import { Button } from '@ifs-gcp/components';

import { STATUS_TO_ERROR_TYPE } from '@/constants/errors';
import { ROUTE_HOME } from '@/constants/router';
import type { ErrorType } from '@/types/errors';

import styles from './error-page.module.scss';

type ErrorPageProps = {
  error?: ErrorBoundaryContextType['error'];
  onReset?: () => void;
};

const isRouterError = (routeError: unknown): routeError is AxiosError | ErrorResponse =>
  !!routeError && (isRouteErrorResponse(routeError) || routeError instanceof AxiosError);

const mapStatusToErrorType = (status: number | undefined | null): ErrorType | null => {
  if (typeof status === 'number' && status in STATUS_TO_ERROR_TYPE) {
    return STATUS_TO_ERROR_TYPE[status];
  }
  return null;
};

const hasErrorType = (value: unknown): value is { errorType: ErrorType } =>
  !!value &&
  typeof value === 'object' &&
  'errorType' in value &&
  isErrorTypeString(value.errorType);

const isErrorTypeString = (value: unknown): value is ErrorType =>
  typeof value === 'string' && ['not_found', 'unauthorized', 'forbidden'].includes(value);

export const ErrorPage = ({ error, onReset }: ErrorPageProps) => {
  const routeError = useRouteError();
  const navigate = useNavigate();

  // routeError comes from the router's errorElement, error comes from the error boundary (check router.tsx)
  // since this component is used in both cases, we need to check both
  const errorType = useMemo<ErrorType | null>(() => {
    if (isRouterError(routeError)) {
      return mapStatusToErrorType(routeError.status);
    }

    if (hasErrorType(routeError)) {
      return routeError.errorType;
    }

    if (isErrorTypeString(error)) {
      return error;
    }

    return null;
  }, [routeError, error]);

  const { t } = useTranslation();

  const errorDetails = useMemo(() => {
    switch (errorType) {
      case 'not_found':
        return {
          title: t('errors.audit_case_not_found'),
          description: t('errors.audit_case_not_found_description'),
          buttonText: t('actions.go_to_dashboard'),
          id: 'audit-case-not-found',
        };
      case 'unauthorized':
      case 'forbidden':
        return {
          title: t('errors.unauthorized'),
          description: t('errors.unauthorized_description'),
          buttonText: t('actions.go_to_home'),
          id: 'unauthorized',
        };
      default:
        return {
          title: t('errors.error_occurred'),
          description: t('errors.error_occurred_description'),
          buttonText: t('actions.go_to_dashboard'),
          id: 'error-occurred',
        };
    }
  }, [errorType, t]);

  const handleButtonClick = () => {
    // Unauthorized users must re-authenticate via platform
    if (errorType === 'unauthorized') {
      window.location.href = import.meta.env.VITE_PLATFORM_WEB_URL;
      return;
    }

    // For all other errors, navigate home and reset error boundary
    navigate(ROUTE_HOME);
    onReset?.();
  };

  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <h6 className={styles.title} data-testid={errorDetails.id}>
          {errorDetails.title}
        </h6>
        <Error className={styles.icon} />
        <p className={styles.description} data-testid={`${errorDetails.id}-description`}>
          {errorDetails.description}
        </p>

        <Button variant="contained" color="secondary" onClick={handleButtonClick}>
          {errorDetails.buttonText}
        </Button>
      </div>
    </div>
  );
};
